import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { US_STATES, findUsState, type UsState } from "@/utils/usStates";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import {
  useCreateLocation,
  useDeleteLocation,
  useSiteLocations,
  useUpdateLocation,
} from "@/api/hooks/locations";
import type { Location } from "@/api/types";

interface FormState {
  display_id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
}

const EMPTY: FormState = {
  display_id: "",
  name: "",
  street: "",
  city: "",
  state: "",
  postal_code: "",
};

function fromLocation(loc: Location): FormState {
  return {
    display_id: loc.display_id,
    name: loc.name,
    street: loc.street ?? "",
    city: loc.city ?? "",
    state: loc.state ?? "",
    postal_code: loc.postal_code ?? "",
  };
}

export default function LocationsTab({ siteId }: { siteId: string }) {
  const { data, isLoading, error } = useSiteLocations(siteId);
  const create = useCreateLocation(siteId);
  const remove = useDeleteLocation(siteId);

  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = useUpdateLocation(editing?.id ?? "", siteId);

  const openCreate = () => {
    setForm(EMPTY);
    setSubmitError(null);
    setCreating(true);
  };
  const openEdit = (loc: Location) => {
    setForm(fromLocation(loc));
    setSubmitError(null);
    setEditing(loc);
  };
  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const submit = async () => {
    setSubmitError(null);
    const payload = {
      display_id: form.display_id.trim(),
      name: form.name.trim(),
      street: form.street.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      postal_code: form.postal_code.trim() || null,
    };
    try {
      if (editing) await update.mutateAsync(payload);
      else await create.mutateAsync(payload);
      close();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Locations
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={openCreate}>
            Add location
          </Button>
        </Box>
        {isLoading && <CircularProgress size={20} />}
        {error && <Alert severity="error">{error.message}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data ?? []).map((loc) => (
                <TableRow key={loc.id} hover>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    <RouterLink
                      to={`/clinops/sites/locations/${loc.id}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      {loc.display_id}
                    </RouterLink>
                  </TableCell>
                  <TableCell>
                    <RouterLink
                      to={`/clinops/sites/locations/${loc.id}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      {loc.name}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{loc.city ?? "—"}</TableCell>
                  <TableCell>{loc.is_active ? "Yes" : "No"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(loc)} aria-label="Edit">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (confirm(`Delete location ${loc.display_id}?`)) {
                          await remove.mutateAsync(loc.id);
                        }
                      }}
                      aria-label="Delete"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(data ?? []).length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No locations yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog open={creating || Boolean(editing)} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit location" : "Add location"}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Display ID"
              value={form.display_id}
              onChange={(e) => setForm({ ...form, display_id: e.target.value })}
              required
              slotProps={{ htmlInput: { maxLength: 32 } }}
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextField
              label="Street"
              value={form.street}
              onChange={(e) => setForm({ ...form, street: e.target.value })}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 1 }}>
              <TextField
                label="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
              <Autocomplete<UsState>
                options={US_STATES.slice()}
                value={findUsState(form.state)}
                onChange={(_, value) =>
                  setForm({ ...form, state: value?.code ?? "" })
                }
                getOptionLabel={(s) => `${s.code} — ${s.name}`}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderInput={(params) => (
                  <TextField {...params} label="State" />
                )}
              />
              <TextField
                label="Postal"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!form.display_id.trim() || !form.name.trim()}
          >
            {editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
